import { Layout } from '@components/common'
import type {
    GetStaticPropsContext,
    GetStaticPropsResult,
    InferGetStaticPropsType
} from 'next'
import s from '@pagestyles/Cms.module.scss'

import getCMSPage from '@pylot-data/api/operations/get-cms'
import { RichContent } from '@corratech/pylot-rich-content'
import React, { ReactElement, useEffect, useState } from 'react'
import type { CmsPage } from '@pylot-data/pylotschema'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useCmsPage } from '@pylot-data/hooks/cms/use-cms-page'
import { GetStaticPaths } from 'next'
import { SEO, SEO_ENUMS } from '@components/seo'
import { ProductItem } from '@components/common/ProductTile/ProductTile'
import { getStoreConfig } from 'config'
import { YotpoScripts } from '@components/alfakher/pylot-yotpo'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { isBlacklisted } from '@lib/utils/blacklistVerify'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'
import { useUser } from '@corratech/pylot-auth-manager'

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        // TODO: Make this dynamic
        paths: [],
        fallback: 'blocking'
    }
}

export async function getStaticProps({
    locale,
    params
}: GetStaticPropsContext): Promise<
    GetStaticPropsResult<{
        cmsPageData: CmsPage | null | undefined
        identifier: string
    }>
> {
    const identifier = params!.identifier as string

    const cmsPageData = await getCMSPage({
        identifier: identifier,
        locale
    })

    if (!cmsPageData || isBlacklisted(identifier)) {
        return {
            notFound: true
        }
    }

    return {
        props: {
            cmsPageData,
            identifier,
            ...(await serverSideTranslations(locale!, ['common']))
        },
        revalidate: 14400
    }
}

const sendIfeameMsg = (message: string, oldmessage?: string) => {
    const iframe = document.getElementById('iframe')
    if (oldmessage === message) {
        return true
    }
    if (iframe) {
        const iframeWindow = (iframe as HTMLIFrameElement).contentWindow
        if (iframeWindow) {
            iframeWindow.postMessage(message, '*')
            return true
        } else {
            console.error('IFrame contentWindow is null.')
        }
    } else {
        console.error('IFrame element not found.')
    }
    return false
}

export default function CMSPage({
    cmsPageData,
    identifier
}: InferGetStaticPropsType<typeof getStaticProps>): ReactElement {
    const config = getStoreConfig()
    const router = useRouter()
    const { t } = useTranslation(['common'])
    const { setPageType } = useAlfakherContext()
    const { customer } = useUser()
    const [currentCustomer, setCurrentCustomer] = useState('')

    const { cmsPage = {} } = useCmsPage(identifier, {
        revalidateOnFocus: false,
        revalidateOnMount: true,
        initialData: { data: { cmsPage: cmsPageData } }
    })

    useEffect(() => {
        setPageType({
            transparentHeader: !!cmsPage?.is_transparent_header,
            isNicotinePage: !!cmsPage?.has_nicotine
        })
    }, [cmsPage])

    useEffect(() => {
        if (router?.query?.identifier === 'contact-us') {
            const checkEmailInterval = setInterval(function () {
                const email = customer ? customer.email : null
                const token = window.localStorage.getItem('pylot_token')

                if (token) {
                    setCurrentCustomer('user log in')
                }

                if (email) {
                    setCurrentCustomer(email)
                    if (sendIfeameMsg(email, currentCustomer)) {
                        clearInterval(checkEmailInterval)
                    }
                } else if (customer === undefined) {
                    if (
                        currentCustomer === '' ||
                        currentCustomer === 'user log in'
                    ) {
                        return
                    }
                    setCurrentCustomer('')
                    sendIfeameMsg('')
                }
            }, 500)

            return () => clearInterval(checkEmailInterval)
        }
    }, [router?.query?.identifier, customer, currentCustomer])

    //route fallback
    if (router.isFallback) {
        return (
            <div className="p-10 mx-auto max-w-8xl">
                {t('Page for this product was not generated beforehand.')}
                <br />
                {t('Loading...')}
            </div>
        )
    }

    return (
        <>
            <SEO cmsPage={cmsPage} page={SEO_ENUMS.CMS} />
            {router?.query?.identifier === 'testimonials' && (
                <YotpoScripts
                    enabled={config.base.yotpo.enabled}
                    apiKey={config.base.yotpo.api_key}
                />
            )}
            <div className={s['cms-page']}>
                <RichContent
                    html={cmsPage?.content}
                    ProductTile={ProductItem}
                />
            </div>
        </>
    )
}

CMSPage.Layout = Layout
